// Workspace tab — the two-ledger reconciliation surface.
//
// Owns selection state, computes suggestion scores via `matchScore` (port of
// `recon-data.jsx`), renders two LedgerColumns and the sticky ActionBar.
// On Reconcile / Set aside, calls the API and bubbles the result up.

import React, { useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TOKENS, UI_FONT, MONO_FONT, NUM_STYLE, CODE_STYLE, laneColor, fmtMoney } from './tokens';
import {
  Icon,
  Mono,
  SelectBox,
  Tone,
  MoneyCell,
  CategoryTag,
  CountChip,
  SumCheck,
  styles,
} from './primitives';
import { matchScore, matchSignals, matchSummary, probableMatches } from './scoring';
import { fetchReconSettings, patchReconSettings } from './api';
import ProbableMatchesModal from './ProbableMatchesModal';

// SAP rows where the invoice number matched exactly but the (USD) amount did
// not — issue #40's "surface, don't bury" bucket. Distinct from fuzzy_low.
const ID_MISMATCH = 'id_match_amount_mismatch';

// Read the precise amount-signal label off a SAP row's match_signals
// (e.g. "Amount off 12.5%" or "FX pending"); fall back to a generic label.
function amountMismatchLabel(row) {
  const sig = (row.match_signals || []).find((s) => s && s.kind === 'amount');
  return (sig && sig.label) || 'amount mismatch';
}

// A row "needs FX" when it's a foreign-currency row with no locked USD value.
// Derived on the wire (no fx_status field) — issue #40 REVISIONS §6.
function needsFx(row) {
  return (
    row.amount_usd == null &&
    !!row.currency &&
    row.currency !== 'USD'
  );
}

// ─── Workspace view ──────────────────────────────────────────────────
export default function WorkspaceView({
  sap = [],
  invoices = [],
  selSap,
  setSelSap,
  selInv,
  setSelInv,
  sapFilter,
  setSapFilter,
  invFilter,
  setInvFilter,
  onReconcile,
  onReconcileBatch,
  onSetAside,
  onToast,
  showSuggestions = true,
  sumPolicy = 'strict',
}) {
  // Per-column sort. Default = amount desc (largest first). The sort
  // chip itself toggles direction; the dropdown picks the field.
  const [sapSort, setSapSort] = React.useState({ field: 'amount', dir: 'desc' });
  const [invSort, setInvSort] = React.useState({ field: 'amount', dir: 'desc' });
  const [showProbable, setShowProbable] = React.useState(false);

  // Manual-review tolerance (USD %, issue #40). Gates which probable matches
  // surface — a wider band shows more (lower-amount-confidence) suggestions.
  // Initialized from the server setting; persisted back on commit (see knob).
  const [tolerancePct, setTolerancePct] = React.useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchReconSettings()
      .then((res) => {
        if (cancelled) return;
        const v = Number(res.data?.recon_manual_review_tolerance_pct);
        if (Number.isFinite(v)) setTolerancePct(v);
      })
      // Endpoint missing / errored → leave the gate off (undefined): the
      // probable-matches surface behaves exactly as it did before #40.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist a committed tolerance value to the server, surfacing the page's
  // toast on failure. Called on blur / Enter / preset click (not per keystroke).
  const commitTolerance = React.useCallback(
    async (pct) => {
      try {
        await patchReconSettings({ recon_manual_review_tolerance_pct: String(pct) });
      } catch (err) {
        onToast?.({
          text:
            err.response?.data?.detail?.message ||
            err.response?.data?.detail ||
            err.message ||
            'Failed to save match tolerance',
          tone: 'miss',
        });
      }
    },
    [onToast]
  );

  // Ranked 1-to-1 candidate pairs for the assisted-reconcile list. Computed
  // from the full unmatched sets (not the filtered/visible rows) so a filter
  // doesn't hide suggestions. Gated by the manual-review tolerance on USD
  // amount-delta once the setting has loaded.
  const probable = useMemo(
    () => probableMatches(sap, invoices, 0.45, tolerancePct == null ? undefined : tolerancePct),
    [sap, invoices, tolerancePct]
  );

  // Filter + sort visible rows.
  const sapVisible = useMemo(
    () =>
      sortSapRows(
        sap.filter((r) => {
          if (!sapFilter) return true;
          const q = sapFilter.toLowerCase();
          return (
            (r.sap_document_number || '').toLowerCase().includes(q) ||
            (r.reference_field || '').toLowerCase().includes(q) ||
            (r.inferred_vendor || '').toLowerCase().includes(q) ||
            (r.text_field || '').toLowerCase().includes(q)
          );
        }),
        sapSort
      ),
    [sap, sapFilter, sapSort]
  );
  const invVisible = useMemo(
    () =>
      sortInvoiceRows(
        invoices.filter((i) => {
          if (!invFilter) return true;
          const q = invFilter.toLowerCase();
          return (
            (i.invoice_number || '').toLowerCase().includes(q) ||
            (i.vendor || '').toLowerCase().includes(q) ||
            (i.project || '').toLowerCase().includes(q)
          );
        }),
        invSort
      ),
    [invoices, invFilter, invSort]
  );

  // Selected entities — by full row, not just id (for sum & scoring).
  const sapSelected = useMemo(
    () => sap.filter((r) => selSap.has(r.id)),
    [sap, selSap]
  );
  const invSelected = useMemo(
    () => invoices.filter((i) => selInv.has(i.id)),
    [invoices, selInv]
  );

  // Cross-side suggestion scores. Compare every selected SAP row against every
  // visible invoice, take the max — this is what determines the colored left
  // border on the *other* side.
  const invSuggestionScores = useMemo(() => {
    const map = new Map();
    if (!showSuggestions) return map;
    sapSelected.forEach((r) =>
      invoices.forEach((i) => {
        const s = matchScore(r, i);
        const prev = map.get(i.id) || 0;
        if (s > prev) map.set(i.id, s);
      })
    );
    return map;
  }, [sapSelected, invoices, showSuggestions]);

  const sapSuggestionScores = useMemo(() => {
    const map = new Map();
    if (!showSuggestions) return map;
    invSelected.forEach((i) =>
      sap.forEach((r) => {
        const s = matchScore(r, i);
        const prev = map.get(r.id) || 0;
        if (s > prev) map.set(r.id, s);
      })
    );
    return map;
  }, [invSelected, sap, showSuggestions]);

  // Toggle helpers.
  const toggleSap = (id) => {
    const next = new Set(selSap);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelSap(next);
  };
  const toggleInv = (id) => {
    const next = new Set(selInv);
    if (next.has(id)) {
      next.delete(id);
      setSelInv(next);
      return;
    }
    // A batch reconciles as a unit, so it's selected exclusively: picking a
    // batch clears everything else, and picking a normal invoice clears any
    // selected batch.
    const row = invoices.find((i) => i.id === id);
    if (row?.is_batch) {
      setSelInv(new Set([id]));
      return;
    }
    const cleaned = new Set(
      [...next].filter((x) => !invoices.find((i) => i.id === x)?.is_batch)
    );
    cleaned.add(id);
    setSelInv(cleaned);
  };
  const clearSel = () => {
    setSelSap(new Set());
    setSelInv(new Set());
  };

  // One-click "confirm the suggested pair" for an id_match_amount_mismatch row:
  // select the SAP row + the invoice the backend matched by number, so the
  // operator lands in the ActionBar ready to reconcile (the same end state a
  // fuzzy_low suggestion reaches via the % chip). No-op if the suggested
  // invoice isn't in the unmatched list (already reconciled / different period).
  const selectSuggestion = (row) => {
    const invId = row.suggested_invoice_id;
    if (!invId || !invoices.find((i) => i.id === invId)) return;
    setSelSap(new Set([row.id]));
    setSelInv(new Set([invId]));
  };

  // ESC clears selection (SPEC §7 "Keyboard").
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') clearSel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sum totals on the selected side(s).
  const sapTotal = sapSelected.reduce((s, r) => s + Number(r.amount || 0), 0);
  const invTotal = invSelected.reduce((s, i) => s + Number(i.amount || 0), 0);

  // The single SAP row, if exactly one is selected — drives signal pills on
  // the invoice side. `pairedInv` is the mirror: the single selected invoice,
  // which drives the % chip tooltip on the SAP side.
  const pairedSap = sapSelected.length === 1 ? sapSelected[0] : null;
  const pairedInv = invSelected.length === 1 ? invSelected[0] : null;

  return (
    <div
      style={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      {/* Assisted-reconcile bar — surface probable leftovers for one-click matching. */}
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '7px 18px',
          background: TOKENS.panel,
          borderBottom: `1px solid ${TOKENS.border}`,
        }}
      >
        <ToleranceKnob
          value={tolerancePct}
          onChange={setTolerancePct}
          onCommit={commitTolerance}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {probable.length > 0 && (
          <Mono size={10} color={TOKENS.ink3}>
            {probable.length} probable match{probable.length === 1 ? '' : 'es'} found
          </Mono>
        )}
        <button
          onClick={() => setShowProbable(true)}
          disabled={probable.length === 0}
          title={
            probable.length
              ? 'Review ranked probable matches and reconcile in one click'
              : 'No probable matches at or above 45%'
          }
          style={{
            ...styles.ghostBtn,
            opacity: probable.length ? 1 : 0.45,
            cursor: probable.length ? 'pointer' : 'default',
          }}
        >
          <Icon d="M9 11l3 3 8-8M3 12a9 9 0 1018 0 9 9 0 00-18 0" size={12} sw={2} />
          Show probable matches
        </button>
        </div>
      </div>

      <div
        style={{
          flex: '1 1 auto',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 0,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* LEFT — SAP unmatched */}
        <LedgerColumn
          side="sap"
          count={sapVisible.length}
          filter={sapFilter}
          setFilter={setSapFilter}
          sort={sapSort}
          setSort={setSapSort}
          rightBorder
        >
          {sapVisible.length === 0 ? (
            <Clear side="sap" total={sap.length} />
          ) : (
            sapVisible.map((row) => (
              <SapRow
                key={row.id}
                row={row}
                selected={selSap.has(row.id)}
                onToggle={() => toggleSap(row.id)}
                suggestionScore={sapSuggestionScores.get(row.id) || 0}
                showSuggestions={showSuggestions}
                pairedWith={pairedInv}
                hasSuggestedInvoice={
                  !!row.suggested_invoice_id &&
                  !!invoices.find((i) => i.id === row.suggested_invoice_id)
                }
                onConfirmSuggestion={() => selectSuggestion(row)}
              />
            ))
          )}
        </LedgerColumn>

        {/* RIGHT — Invoices unmatched */}
        <LedgerColumn
          side="inv"
          count={invVisible.length}
          filter={invFilter}
          setFilter={setInvFilter}
          sort={invSort}
          setSort={setInvSort}
        >
          {invVisible.length === 0 ? (
            <Clear side="inv" total={invoices.length} />
          ) : (
            invVisible.map((inv) => (
              <InvoiceRow
                key={inv.id}
                inv={inv}
                selected={selInv.has(inv.id)}
                onToggle={() => toggleInv(inv.id)}
                suggestionScore={invSuggestionScores.get(inv.id) || 0}
                showSuggestions={showSuggestions}
                pairedWith={pairedSap}
              />
            ))
          )}
        </LedgerColumn>
      </div>

      <ActionBar
        sapSel={sapSelected}
        invSel={invSelected}
        sapTotal={sapTotal}
        invTotal={invTotal}
        onClear={clearSel}
        onReconcile={async () => {
          // Batch selection is exclusive (see toggleInv), so the invoice side
          // is either a single batch or a set of normal invoices.
          const batch = invSelected.find((i) => i.is_batch);
          const ok = batch
            ? await onReconcileBatch(
                sapSelected.map((r) => r.id),
                batch.batch_kind,
                batch.id
              )
            : await onReconcile(
                sapSelected.map((r) => r.id),
                invSelected.map((i) => i.id)
              );
          if (ok) clearSel();
        }}
        onSetAside={async (reason) => {
          // Set-aside is SAP-side only in v1. SPEC §4.3.
          if (sapSelected.length === 0) return;
          const ok = await onSetAside(
            sapSelected.map((r) => r.id),
            reason
          );
          if (ok) clearSel();
        }}
        sumPolicy={sumPolicy}
      />

      {showProbable && (
        <ProbableMatchesModal
          matches={probable}
          onReconcile={onReconcile}
          onClose={() => setShowProbable(false)}
        />
      )}
    </div>
  );
}

// ─── Ledger column ───────────────────────────────────────────────────
function LedgerColumn({ side, count, filter, setFilter, sort, setSort, rightBorder, children }) {
  const hue = side === 'sap' ? 222 : 260;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
        borderRight: rightBorder ? `1px solid ${TOKENS.border}` : 'none',
        background: TOKENS.bg,
      }}
    >
      <div
        style={{
          padding: '10px 14px 8px',
          background: TOKENS.panel,
          borderBottom: `1px solid ${TOKENS.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flex: '0 0 auto',
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: laneColor(hue, 0.62, 0.14),
            flex: '0 0 auto',
          }}
        />
        <Mono size={10.5} color={TOKENS.ink3}>
          {side === 'sap' ? 'SAP ROWS · UNMATCHED' : 'INVOICES · UNMATCHED'}
        </Mono>
        <span
          style={{
            ...NUM_STYLE,
            fontSize: 10.5,
            fontWeight: 600,
            color: TOKENS.ink,
            background: TOKENS.panelAlt,
            border: `1px solid ${TOKENS.border}`,
            padding: '1px 7px',
            borderRadius: 9,
          }}
        >
          {count}
        </span>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            flex: '1 1 auto',
            justifyContent: 'flex-end',
            minWidth: 0,
          }}
        >
          <SortSelect value={sort} onChange={setSort} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 8px',
              borderRadius: 6,
              background: TOKENS.panelAlt,
              border: `1px solid ${TOKENS.border}`,
              flex: '1 1 auto',
              maxWidth: 220,
              minWidth: 120,
            }}
          >
            <Icon
              d="M11 19a8 8 0 1 1 5.3-14M21 21l-4.3-4.3"
              size={11}
              sw={2}
              stroke={TOKENS.ink3}
            />
            <input
              value={filter || ''}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter…"
              style={{
                flex: 1,
                background: 'transparent',
                border: 'none',
                outline: 'none',
                fontFamily: UI_FONT,
                fontSize: 11,
                color: TOKENS.ink,
                minWidth: 0,
              }}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          flex: '1 1 auto',
          overflow: 'auto',
          padding: '8px 14px 80px',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        {children}
      </div>
    </div>
  );
}

// ─── SAP row ─────────────────────────────────────────────────────────
function SapRow({
  row,
  selected,
  onToggle,
  suggestionScore,
  showSuggestions,
  pairedWith,
  hasSuggestedInvoice,
  onConfirmSuggestion,
}) {
  const suggest = showSuggestions && suggestionScore >= 0.45;
  const strong = suggestionScore >= 0.7;
  // Tooltip on the % chip: what actually lines up with the single selected
  // invoice (null when 0 or 2+ invoices are selected — the score is then a
  // max across them and can't name one pair).
  const scoreTip = pairedWith ? matchSummary(row, pairedWith) : undefined;
  // id_match_amount_mismatch — invoice number matched exactly but the amount
  // didn't. A strong, dedicated signal (issue #40): tint the row danger so it
  // reads differently from a fuzzy_low (warn) suggestion at a glance.
  const idMismatch = row.match_category === ID_MISMATCH;
  const accent = idMismatch ? TOKENS.danger : strong ? TOKENS.ok : suggest ? TOKENS.warn : null;
  const inferred = row.inferred_vendor || '';
  const isUnknownVendor = !inferred || inferred === '—';
  const showFx = needsFx(row);
  const usd = row.amount_usd != null ? Number(row.amount_usd) : null;
  const showUsdLine = row.currency && row.currency !== 'USD' && usd != null;

  // Map the API transaction_category → design category id for the chip.
  // Both already use the same names ('vendor_invoice' etc.) so this is just
  // a passthrough; fall back to 'other'.
  const categoryId = row.transaction_category || 'other';

  return (
    <div
      onClick={onToggle}
      style={{
        cursor: 'pointer',
        background: selected ? TOKENS.selectSoft : TOKENS.panel,
        border: `1px solid ${selected ? TOKENS.select : accent || TOKENS.border}`,
        borderLeft: accent
          ? `3px solid ${accent}`
          : selected
          ? `3px solid ${TOKENS.select}`
          : `3px solid ${TOKENS.border}`,
        borderRadius: 7,
        padding: '9px 11px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        boxShadow: selected ? `inset 0 0 0 1px ${TOKENS.select}` : 'none',
      }}
    >
      <div style={{ paddingTop: 2 }}>
        <SelectBox checked={selected} onChange={onToggle} size={13} />
      </div>
      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Line 1: vendor + category + (suggestion pill) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '-0.005em',
              color: isUnknownVendor ? TOKENS.ink3 : TOKENS.ink,
              fontStyle: isUnknownVendor ? 'italic' : 'normal',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 0',
              minWidth: 0,
            }}
          >
            {isUnknownVendor ? 'unknown vendor' : inferred}
          </span>
          <span style={{ flex: '0 0 auto' }}>
            <CategoryTag id={categoryId} />
          </span>
          {idMismatch && (
            <span style={{ flex: '0 0 auto' }}>
              <Tone
                tone="miss"
                icon={<Icon d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" size={9} sw={2} />}
                title={
                  'The invoice number matched exactly but the (USD) amount did not — ' +
                  'review before reconciling.'
                }
              >
                Invoice # matches · {amountMismatchLabel(row)}
              </Tone>
            </span>
          )}
          {suggest && (
            <span style={{ flex: '0 0 auto' }}>
              <Tone
                tone={strong ? 'good' : 'soft'}
                icon={<Icon d="M5 12l4 4 10-10" size={9} sw={3} />}
                title={scoreTip}
              >
                {Math.round(suggestionScore * 100)}%
              </Tone>
            </span>
          )}
        </div>
        {/* Line 2: reference + text */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            minWidth: 0,
            fontSize: 11,
            color: TOKENS.ink2,
          }}
        >
          <Mono size={9} color={TOKENS.ink3} style={{ flex: '0 0 auto' }}>
            REF
          </Mono>
          <span
            style={{
              ...CODE_STYLE,
              flex: '0 0 auto',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '45%',
            }}
          >
            {row.reference_field || '—'}
          </span>
          <span style={{ color: TOKENS.ink3, flex: '0 0 auto' }}>·</span>
          <span
            style={{
              flex: '1 1 0',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {row.text_field || ''}
          </span>
        </div>
        {/* Line 3: docNo + (for an id_match_amount_mismatch) a one-click jump to
            the suggested invoice so the operator can confirm the manual match. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Mono size={9.5} color={TOKENS.ink3} style={CODE_STYLE}>
            {row.sap_document_number}
          </Mono>
          {idMismatch && hasSuggestedInvoice && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onConfirmSuggestion();
              }}
              title="Select this row and the invoice it matched by number, ready to reconcile"
              style={{
                ...styles.ghostBtn,
                padding: '2px 8px',
                fontSize: 10,
                color: TOKENS.danger,
                borderColor: TOKENS.danger,
              }}
            >
              <Icon d="M5 12l4 4 10-10" size={9} sw={2.5} />
              Review match
            </button>
          )}
        </div>
      </div>
      <DateColumn label="POSTED" value={row.posting_date} />
      <AmountColumn
        amount={row.amount}
        currency={row.currency || 'EUR'}
        usd={showUsdLine ? usd : null}
        showFx={showFx}
      />
    </div>
  );
}

// Right-aligned amount stack: native amount, plus (for non-USD rows) the
// locked USD value underneath, or a small "needs FX" flag when no USD value
// is locked yet. Issue #40 — keeps the match basis (USD) visible to operators.
function AmountColumn({ amount, currency, usd, showFx }) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        paddingTop: 1,
        width: 130,
        textAlign: 'right',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 2,
      }}
    >
      <MoneyCell value={Number(amount)} currency={currency} size={13} />
      {usd != null && (
        <Mono size={10} color={TOKENS.ink3} title="Locked USD value used for matching">
          {fmtMoney(usd, 'USD')}
        </Mono>
      )}
      {showFx && (
        <Tone
          tone="info"
          title="No locked USD value yet — this row needs an FX rate before it can match on amount."
        >
          needs FX
        </Tone>
      )}
    </div>
  );
}

// ─── Invoice row ─────────────────────────────────────────────────────
function InvoiceRow({
  inv,
  selected,
  onToggle,
  suggestionScore,
  showSuggestions,
  pairedWith,
}) {
  const suggest = showSuggestions && suggestionScore >= 0.45;
  const strong = suggestionScore >= 0.7;
  const accent = strong ? TOKENS.ok : suggest ? TOKENS.warn : null;
  const signals = pairedWith && suggest ? matchSignals(pairedWith, inv) : null;
  const scoreTip = pairedWith ? matchSummary(pairedWith, inv) : undefined;
  const invUsd = inv.amount_usd != null ? Number(inv.amount_usd) : null;
  const showInvUsd = inv.currency && inv.currency !== 'USD' && invUsd != null;
  const navigate = useNavigate();
  // Open the invoice detail without toggling the row's selection.
  const openInvoice = (e) => {
    e.stopPropagation();
    navigate(`/invoice/${inv.id}`);
  };
  return (
    <div
      onClick={onToggle}
      style={{
        cursor: 'pointer',
        background: selected ? TOKENS.selectSoft : TOKENS.panel,
        border: `1px solid ${selected ? TOKENS.select : accent || TOKENS.border}`,
        borderLeft: accent
          ? `3px solid ${accent}`
          : selected
          ? `3px solid ${TOKENS.select}`
          : `3px solid ${TOKENS.border}`,
        borderRadius: 7,
        padding: '9px 11px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        boxShadow: selected ? `inset 0 0 0 1px ${TOKENS.select}` : 'none',
      }}
    >
      <div style={{ paddingTop: 2 }}>
        <SelectBox checked={selected} onChange={onToggle} size={13} />
      </div>
      <div
        style={{
          flex: '1 1 0',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {/* Line 1: vendor + project + suggestion */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '-0.005em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flex: '1 1 0',
              minWidth: 0,
            }}
          >
            {inv.vendor}
          </span>
          {inv.is_batch && (
            <span style={{ flex: '0 0 auto' }}>
              <Tone tone="info">{inv.batch_kind === 'lux' ? 'LUX BATCH' : 'VAT BATCH'}</Tone>
            </span>
          )}
          {inv.project && (
            <Mono size={9.5} color={TOKENS.ink3} style={{ flex: '0 0 auto' }}>
              {inv.project}
            </Mono>
          )}
          {suggest && (
            <span style={{ flex: '0 0 auto' }}>
              <Tone
                tone={strong ? 'good' : 'soft'}
                icon={<Icon d="M5 12l4 4 10-10" size={9} sw={3} />}
                title={scoreTip}
              >
                {Math.round(suggestionScore * 100)}%
              </Tone>
            </span>
          )}
        </div>
        {/* Line 2: sent to AP only — invoiced date moved to the right
            stack so it aligns vertically with the SAP card's date. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
            fontSize: 11,
            color: TOKENS.ink2,
            flexWrap: 'wrap',
          }}
        >
          {inv.sent_to_ap_at ? (
            <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4 }}>
              <Mono size={9} color={TOKENS.ink3}>SENT TO AP</Mono>
              <Mono size={10.5}>{inv.sent_to_ap_at}</Mono>
            </span>
          ) : (
            <Mono size={9} color={TOKENS.ink3}>NOT YET SENT</Mono>
          )}
        </div>
        {/* Line 3: invoice number — a link to the invoice detail, plus an
            open icon. Both stop propagation so they don't toggle selection.
            Batch rows have no single invoice to open, so they show a count. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {inv.is_batch ? (
            <Mono size={9.5} color={TOKENS.ink3}>
              {inv.invoice_count} invoice{inv.invoice_count === 1 ? '' : 's'} in batch
            </Mono>
          ) : (
            <>
              <span
                onClick={openInvoice}
                title="Open invoice"
                style={{
                  ...CODE_STYLE,
                  fontSize: 9.5,
                  color: TOKENS.accent,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  textUnderlineOffset: 2,
                }}
              >
                {inv.invoice_number}
              </span>
              <span
                onClick={openInvoice}
                title="Open invoice"
                style={{ display: 'inline-flex', alignItems: 'center', color: TOKENS.ink3, cursor: 'pointer' }}
              >
                <Icon
                  d="M14 4h6v6 M20 4l-8 8 M19 13v6a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h6"
                  size={11}
                  sw={1.8}
                />
              </span>
            </>
          )}
        </div>
        {/* Signal pills (only when exactly one SAP is selected & this invoice
            scores ≥ 0.45). */}
        {signals && signals.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
            {signals.map((s, i) => (
              <Tone key={i} tone={s.tone} title={s.tip}>
                {s.label}
              </Tone>
            ))}
          </div>
        )}
      </div>
      <DateColumn label="INVOICED" value={inv.invoice_date} />
      <AmountColumn
        amount={inv.amount}
        currency={inv.currency || 'EUR'}
        usd={showInvUsd ? invUsd : null}
        showFx={needsFx(inv)}
      />
    </div>
  );
}

// Fixed-width date "column" inside each row. Keeps the date value at the
// same x-coordinate down a ledger so the user can scan dates top-to-
// bottom without them jiggling when amount widths change.
function DateColumn({ label, value }) {
  return (
    <div
      style={{
        flex: '0 0 auto',
        width: 96,
        paddingTop: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 1,
        borderLeft: `1px solid ${TOKENS.border}`,
        paddingLeft: 10,
      }}
    >
      <Mono size={9} color={TOKENS.ink3}>{label}</Mono>
      <Mono size={11} color={TOKENS.ink2}>{value || '—'}</Mono>
    </div>
  );
}

// ─── Empty-column "All clear" pane ──────────────────────────────────
function Clear({ side, total }) {
  return (
    <div
      style={{
        padding: '30px 18px',
        textAlign: 'center',
        borderRadius: 8,
        background: 'oklch(0.96 0.04 150)',
        border: '1px dashed oklch(0.78 0.10 150)',
        color: 'oklch(0.32 0.10 150)',
        fontFamily: UI_FONT,
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600 }}>All clear</div>
      <div style={{ fontSize: 11.5, marginTop: 6 }}>
        {total === 0
          ? 'Nothing to reconcile in this period yet.'
          : `Every ${side === 'sap' ? 'SAP row' : 'invoice'} in this period has a counterpart.`}
      </div>
    </div>
  );
}

// ─── Sticky action bar (3 states) ───────────────────────────────────
function ActionBar({
  sapSel,
  invSel,
  sapTotal,
  invTotal,
  onClear,
  onReconcile,
  onSetAside,
  sumPolicy,
}) {
  const [setAsideOpen, setSetAsideOpen] = React.useState(false);
  const [reason, setReason] = React.useState('');

  const nothing = sapSel.length === 0 && invSel.length === 0;
  const oneSided =
    (sapSel.length > 0 && invSel.length === 0) ||
    (invSel.length > 0 && sapSel.length === 0);
  const both = sapSel.length > 0 && invSel.length > 0;

  const delta = Math.abs(sapTotal - invTotal);
  const maxAmt = Math.max(Math.abs(sapTotal), Math.abs(invTotal));
  const sumOk = both && delta < 0.01;
  const sumClose =
    both && !sumOk && maxAmt > 0 && delta / maxAmt < 0.01;

  // Strict policy: must be exact (< 0.01). Lenient policy: close ratio
  // (delta/max < 0.01) is acceptable.
  const reconcileEnabled = both && (sumPolicy === 'lenient' ? sumOk || sumClose : sumOk);

  const actionBarStyle = {
    position: 'sticky',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 30,
    background: TOKENS.ink,
    color: TOKENS.headerInk,
    borderTop: '1px solid oklch(0.30 0.012 260)',
    padding: '10px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: '0 0 auto',
    flexWrap: 'wrap',
  };

  // Reset the reason editor whenever the selection drops to nothing.
  React.useEffect(() => {
    if (nothing) {
      setSetAsideOpen(false);
      setReason('');
    }
  }, [nothing]);

  if (nothing) {
    return (
      <div style={actionBarStyle}>
        <Icon d="M4 6h16M4 12h16M4 18h12" size={14} stroke="oklch(0.72 0.01 250)" sw={2} />
        <span style={{ fontFamily: UI_FONT, fontSize: 12, color: 'oklch(0.82 0.006 250)' }}>
          Select one or more rows on each side to reconcile.
        </span>
        <span
          style={{
            marginLeft: 'auto',
            fontFamily: MONO_FONT,
            fontSize: 10,
            color: 'oklch(0.62 0.01 250)',
          }}
        >
          Auto-match runs after every upload · Esc clears selection
        </span>
      </div>
    );
  }

  return (
    <div style={actionBarStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <CountChip n={sapSel.length} label="SAP" hue={222} />
        <span style={{ color: 'oklch(0.50 0.01 250)' }}>·</span>
        <CountChip n={invSel.length} label="INV" hue={260} />
      </div>

      {both && <SumCheck sapTotal={sapTotal} invTotal={invTotal} />}

      {oneSided && !setAsideOpen && (
        <span
          style={{
            fontFamily: UI_FONT,
            fontSize: 11.5,
            color: 'oklch(0.78 0.006 250)',
          }}
        >
          {sapSel.length > 0
            ? 'Pick at least one invoice — or set aside as “no invoice expected”'
            : 'Pick at least one SAP row to match'}
        </span>
      )}

      {oneSided && setAsideOpen && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '1 1 auto' }}>
          <span style={{ fontFamily: UI_FONT, fontSize: 11.5, color: 'oklch(0.82 0.006 250)' }}>
            Reason:
          </span>
          <input
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="No invoice expected — e.g. payroll posting"
            style={{
              flex: '1 1 auto',
              padding: '5px 9px',
              borderRadius: 5,
              border: '1px solid oklch(0.32 0.012 260)',
              background: 'oklch(0.24 0.012 260)',
              color: '#fff',
              fontFamily: UI_FONT,
              fontSize: 11.5,
              outline: 'none',
              minWidth: 200,
            }}
          />
        </div>
      )}

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <button onClick={onClear} style={styles.ghostDarkBtn}>
          Clear
        </button>
        {oneSided && sapSel.length > 0 && !setAsideOpen && (
          <button onClick={() => setSetAsideOpen(true)} style={styles.ghostDarkBtn}>
            <Icon d="M9 12h6 M5 7l14 10" size={12} sw={2} />
            Set aside
          </button>
        )}
        {oneSided && setAsideOpen && (
          <>
            <button
              onClick={() => {
                setSetAsideOpen(false);
                setReason('');
              }}
              style={styles.ghostDarkBtn}
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                if (!reason.trim()) return;
                await onSetAside(reason.trim());
                setSetAsideOpen(false);
                setReason('');
              }}
              disabled={!reason.trim()}
              style={{
                ...styles.primaryBigBtn,
                background: reason.trim() ? TOKENS.warn : 'oklch(0.38 0.012 260)',
                color: reason.trim() ? '#211504' : 'oklch(0.78 0.006 250)',
                cursor: reason.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <Icon d="M5 12l4 4 10-10" size={13} sw={2.5} />
              Confirm set-aside
            </button>
          </>
        )}
        {both && (
          <button
            onClick={onReconcile}
            disabled={!reconcileEnabled}
            style={{
              ...styles.primaryBigBtn,
              background: sumOk
                ? TOKENS.ok
                : sumClose && sumPolicy === 'lenient'
                ? TOKENS.warn
                : 'oklch(0.38 0.012 260)',
              cursor: reconcileEnabled ? 'pointer' : 'not-allowed',
              opacity: reconcileEnabled ? 1 : 0.6,
              color: sumOk
                ? '#062015'
                : sumClose && sumPolicy === 'lenient'
                ? '#211504'
                : 'oklch(0.78 0.006 250)',
            }}
          >
            <Icon d="M5 12l4 4 10-10" size={14} sw={2.5} />
            {sumOk
              ? 'Reconcile'
              : sumClose && sumPolicy === 'lenient'
              ? 'Reconcile (with delta)'
              : `Sum mismatch · Δ ${fmtMoney(invTotal - sapTotal)}`}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Tolerance knob ──────────────────────────────────────────────────
//
// The manual-review tolerance (USD %). A single small numeric input with a
// few quick presets — deliberately minimal (issue #40 REVISIONS §7). Changing
// it (a) re-gates the client probable-matches surface by USD amount-delta and
// (b) persists `recon_manual_review_tolerance_pct` on commit (blur / Enter /
// preset click — not per keystroke). Hidden until the server value loads.
const TOLERANCE_PRESETS = [1, 2, 5];

function ToleranceKnob({ value, onChange, onCommit }) {
  // Local string mirror so the operator can type freely (incl. a transient
  // empty box) without the numeric gate flickering on every keystroke.
  const [text, setText] = React.useState(value == null ? '' : String(value));
  React.useEffect(() => {
    setText(value == null ? '' : String(value));
  }, [value]);

  if (value == null) return <span />; // setting not loaded — keep the bar balanced

  const commit = () => {
    const n = Number(text);
    if (!Number.isFinite(n) || n < 0) {
      // Reject garbage / negatives: revert the box to the live value.
      setText(String(value));
      return;
    }
    if (n !== value) {
      onChange(n);
      onCommit(n);
    }
  };
  const pickPreset = (p) => {
    setText(String(p));
    if (p !== value) {
      onChange(p);
      onCommit(p);
    }
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flex: '0 0 auto',
      }}
      title="Widens which suggestions appear for manual review (compared on locked USD amounts)."
    >
      <Mono size={9.5} color={TOKENS.ink3}>
        MATCH TOLERANCE (USD %)
      </Mono>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '3px 6px',
          borderRadius: 6,
          background: TOKENS.panelAlt,
          border: `1px solid ${TOKENS.border}`,
        }}
      >
        <input
          type="number"
          min="0"
          step="0.5"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          style={{
            width: 44,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            textAlign: 'right',
            ...NUM_STYLE,
            fontSize: 11,
            color: TOKENS.ink,
          }}
        />
        <span style={{ fontFamily: MONO_FONT, fontSize: 11, color: TOKENS.ink3 }}>%</span>
      </div>
      <div style={{ display: 'inline-flex', gap: 3 }}>
        {TOLERANCE_PRESETS.map((p) => {
          const active = Number(value) === p;
          return (
            <button
              key={p}
              onClick={() => pickPreset(p)}
              style={{
                ...styles.ghostBtn,
                padding: '2px 7px',
                fontSize: 10,
                background: active ? TOKENS.accentSoft : TOKENS.panel,
                color: active ? TOKENS.accent : TOKENS.ink2,
                borderColor: active ? TOKENS.accent : TOKENS.border,
              }}
            >
              {p}%
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sort select ─────────────────────────────────────────────────────
//
// Tiny inline dropdown shown in each LedgerColumn header. Options match
// the three things you'd actually want to sort by: name (alpha), date
// (newest first), amount (largest first).

function SortSelect({ value, onChange }) {
  const { field, dir } = value || { field: 'amount', dir: 'desc' };
  const toggleDir = (e) => {
    // Don't toggle when the click came from the <select> itself.
    if (e.target.tagName === 'SELECT' || e.target.tagName === 'OPTION') return;
    onChange({ field, dir: dir === 'desc' ? 'asc' : 'desc' });
  };
  return (
    <div
      onClick={toggleDir}
      title={`Sort ${dir === 'desc' ? 'descending' : 'ascending'} — click to toggle`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '4px 8px',
        borderRadius: 6,
        background: TOKENS.panelAlt,
        border: `1px solid ${TOKENS.border}`,
        flex: '0 0 auto',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Icon
        // Down arrow for desc (largest/newest/Z first), up arrow for asc.
        d={dir === 'desc' ? 'M12 5v14M5 12l7 7 7-7' : 'M12 19V5M5 12l7-7 7 7'}
        size={11}
        sw={2}
        stroke={TOKENS.ink2}
      />
      <Mono size={9} color={TOKENS.ink3}>
        SORT
      </Mono>
      <select
        value={field}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange({ field: e.target.value, dir })}
        style={{
          background: 'transparent',
          border: 'none',
          outline: 'none',
          fontFamily: UI_FONT,
          fontSize: 11,
          color: TOKENS.ink,
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <option value="amount">Amount</option>
        <option value="date">Date</option>
        <option value="name">Name</option>
      </select>
    </div>
  );
}

// ─── Sort helpers ────────────────────────────────────────────────────
//
// Compare on the chosen field; flip the result for ascending.

function sortSapRows(rows, sort) {
  const { field, dir } = sort || { field: 'amount', dir: 'desc' };
  const flip = dir === 'asc' ? -1 : 1;
  const copy = rows.slice();
  if (field === 'name') {
    copy.sort((a, b) =>
      -flip *
      (a.inferred_vendor || 'zzz').localeCompare(b.inferred_vendor || 'zzz')
    );
  } else if (field === 'date') {
    copy.sort(
      (a, b) =>
        flip * (b.posting_date || '').localeCompare(a.posting_date || '')
    );
  } else {
    // amount — by absolute value so clearings sort with their non-negated peers
    copy.sort(
      (a, b) =>
        flip * (Math.abs(Number(b.amount || 0)) - Math.abs(Number(a.amount || 0)))
    );
  }
  return copy;
}

function sortInvoiceRows(rows, sort) {
  const { field, dir } = sort || { field: 'amount', dir: 'desc' };
  const flip = dir === 'asc' ? -1 : 1;
  const copy = rows.slice();
  if (field === 'name') {
    copy.sort(
      (a, b) => -flip * (a.vendor || 'zzz').localeCompare(b.vendor || 'zzz')
    );
  } else if (field === 'date') {
    copy.sort(
      (a, b) =>
        flip * (b.invoice_date || '').localeCompare(a.invoice_date || '')
    );
  } else {
    copy.sort(
      (a, b) =>
        flip * (Math.abs(Number(b.amount || 0)) - Math.abs(Number(a.amount || 0)))
    );
  }
  return copy;
}
